import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls';
import { AnalysisTab } from '@/components/home/home';
import { getSamplingPlaneStats, updateSamplingPlaneFromTransform, queryRaysAtLocalPoint } from '@/utils/viewAnalyser';
import { useThreeSceneStore } from '@/store/threeSceneStore';

interface ViewAnalysisPlaneControlsParams {
	canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
	cameraRef: React.MutableRefObject<THREE.Camera | null>;
	sceneRef: React.MutableRefObject<THREE.Scene | null>;
	controlsRef: React.MutableRefObject<{ controls: OrbitControls } | null>;
	sceneInitialized: boolean;
	sceneVersion: number;
	tab: AnalysisTab;
}

const TERRAIN_CLEARANCE = 0.1;
const TERRAIN_RAY_START_Z = 10000;

const getTerrainMeshes = (scene: THREE.Scene): THREE.Object3D[] => {
	const terrainMeshes: THREE.Object3D[] = [];
	scene.traverse((obj) => {
		if (!(obj instanceof THREE.Mesh) || !obj.visible) {
			return;
		}

		const isTerrainByName = obj.name.toLowerCase().includes('terrain');
		const isTerrainByUserData =
			obj.userData.category === 'terrain' ||
			obj.userData.type === 'terrain' ||
			obj.userData.isTerrain === true;

		if (isTerrainByName || isTerrainByUserData) {
			terrainMeshes.push(obj);
		}
	});

	return terrainMeshes;
};

const getBottomEdgeSamplePoints = (object: THREE.Object3D): THREE.Vector3[] => {
	if (!(object instanceof THREE.Mesh)) {
		return [object.getWorldPosition(new THREE.Vector3())];
	}

	const geometry = object.geometry;
	if (!geometry.boundingBox) {
		geometry.computeBoundingBox();
	}

	const bounds = geometry.boundingBox;
	if (!bounds) {
		return [object.getWorldPosition(new THREE.Vector3())];
	}

	const localCorners = [
		new THREE.Vector3(bounds.min.x, bounds.min.y, 0),
		new THREE.Vector3(bounds.max.x, bounds.min.y, 0),
		new THREE.Vector3(bounds.max.x, bounds.max.y, 0),
		new THREE.Vector3(bounds.min.x, bounds.max.y, 0),
	];

	const worldCorners = localCorners
		.map((point) => point.clone().applyMatrix4(object.matrixWorld))
		.sort((a, b) => a.z - b.z);

	const edgeA = worldCorners[0];
	const edgeB = worldCorners[1];
	const edgeMid = edgeA.clone().add(edgeB).multiplyScalar(0.5);

	return [edgeA, edgeB, edgeMid];
};

const getTerrainHeightAt = (
	x: number,
	y: number,
	terrainMeshes: THREE.Object3D[],
	raycaster: THREE.Raycaster
): number | null => {
	raycaster.ray.origin.set(x, y, TERRAIN_RAY_START_Z);
	raycaster.ray.direction.set(0, 0, -1);
	raycaster.near = 0;
	raycaster.far = TERRAIN_RAY_START_Z * 2;

	const hits = raycaster.intersectObjects(terrainMeshes, true);
	if (hits.length === 0) {
		return null;
	}

	return hits[0].point.z;
};

const clampObjectAboveTerrain = (
	object: THREE.Object3D,
	scene: THREE.Scene,
	precomputedTerrainMeshes?: THREE.Object3D[]
): boolean => {
	const terrainMeshes = precomputedTerrainMeshes ?? getTerrainMeshes(scene);
	if (terrainMeshes.length === 0) {
		return false;
	}

	object.updateMatrixWorld(true);
	const samplePoints = getBottomEdgeSamplePoints(object);
	const raycaster = new THREE.Raycaster();

	let requiredRaise = 0;
	for (const sample of samplePoints) {
		const terrainHeight = getTerrainHeightAt(sample.x, sample.y, terrainMeshes, raycaster);
		if (terrainHeight === null) {
			continue;
		}

		const minAllowedZ = terrainHeight + TERRAIN_CLEARANCE;
		requiredRaise = Math.max(requiredRaise, minAllowedZ - sample.z);
	}

	if (requiredRaise <= 0) {
		return false;
	}

	const worldPosition = object.getWorldPosition(new THREE.Vector3());
	worldPosition.z += requiredRaise;
	if (object.parent) {
		object.parent.worldToLocal(worldPosition);
		object.position.copy(worldPosition);
	} else {
		object.position.copy(worldPosition);
	}

	object.updateMatrixWorld(true);
	return true;
};

const markIgnoreSceneClick = (object: THREE.Object3D) => {
	object.userData.ignoreSceneClick = true;
	object.traverse((child) => {
		child.userData.ignoreSceneClick = true;
	});
};

export const useViewAnalysisPlaneControls = (params: ViewAnalysisPlaneControlsParams) => {
	const { canvasRef, cameraRef, sceneRef, controlsRef, sceneInitialized, sceneVersion, tab } =
		params;
	const sampleScale = useThreeSceneStore((state) => state.viewPlaneSampleScale);
	const transformRef = useRef<TransformControls | null>(null);
	const inspectorMarkerRef = useRef<THREE.Object3D | null>(null);
	const activeSampleRef = useRef<{ planeMesh: THREE.Mesh; localX: number; localY: number } | null>(null);
	const isDraggingRef = useRef(false);
	const frameRequestRef = useRef<number | null>(null);
	const pendingObjectRef = useRef<THREE.Object3D | null>(null);
	const lastDragUpdateMsRef = useRef(0);

	const removeInspectorMarker = () => {
		if (inspectorMarkerRef.current) {
			if (inspectorMarkerRef.current.parent) {
				inspectorMarkerRef.current.parent.remove(inspectorMarkerRef.current);
			}
			inspectorMarkerRef.current.traverse((child) => {
				if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) {
					child.geometry?.dispose();
					if (child.material instanceof THREE.Material) child.material.dispose();
				}
			});
			inspectorMarkerRef.current = null;
		}
	};

	const placeInspectorMarker = (planeMesh: THREE.Mesh, localX: number, localY: number, radius: number) => {
		removeInspectorMarker();
		const markerGroup = new THREE.Group();
		markerGroup.userData.ignoreSceneClick = true;

		const r = radius;

		// Outer ring
		const ring = new THREE.Mesh(
			new THREE.RingGeometry(r * 0.95, r, 40),
			new THREE.MeshBasicMaterial({
				color: 0xffffff,
				side: THREE.DoubleSide,
				transparent: true,
				opacity: 0.9,
				depthWrite: false,
			})
		);
		ring.renderOrder = 20;
		ring.userData.ignoreSceneClick = true;

		// Crosshair lines as LineSegments
		const cr = r * 1.3;
		const crossPositions = new Float32Array([
			-cr, 0, 0,  cr, 0, 0,
			0, -cr, 0,  0, cr, 0,
		]);
		const cross = new THREE.LineSegments(
			new THREE.BufferGeometry().setAttribute('position', new THREE.BufferAttribute(crossPositions, 3)),
			new THREE.LineBasicMaterial({
				color: 0xffffff,
				transparent: true,
				opacity: 0.7,
				depthWrite: false,
			})
		);
		cross.renderOrder = 20;
		cross.userData.ignoreSceneClick = true;

		markerGroup.add(ring);
		markerGroup.add(cross);
		markerGroup.position.set(localX, localY, 0.015);
		planeMesh.add(markerGroup);
		inspectorMarkerRef.current = markerGroup;
	};

	const updateInspectorForSample = (
		planeMesh: THREE.Mesh,
		localX: number,
		localY: number,
		scale: number
	) => {
		if (!planeMesh.parent) {
			activeSampleRef.current = null;
			removeInspectorMarker();
			useThreeSceneStore.getState().setViewPlaneInspector(null);
			return;
		}

		const planeWidth = (planeMesh.userData.currentWidth as number | undefined) ?? 20;
		const planeHeight = (planeMesh.userData.currentHeight as number | undefined) ?? 10;
		const sampleRadius = Math.max(0.5, Math.max(planeWidth, planeHeight) * 0.07 * scale);
		const nearbyRays = queryRaysAtLocalPoint(planeMesh, localX, localY, scale);
		if (nearbyRays.length === 0) {
			removeInspectorMarker();
			useThreeSceneStore.getState().setViewPlaneInspector(null);
			return;
		}

		placeInspectorMarker(planeMesh, localX, localY, sampleRadius);
		const catMap = new Map<string, { count: number; blockedCount: number; openCount: number; totalDist: number; color: string }>();
		for (const ray of nearbyRays) {
			let acc = catMap.get(ray.category);
			if (!acc) {
				acc = { count: 0, blockedCount: 0, openCount: 0, totalDist: 0, color: ray.cssColor };
				catMap.set(ray.category, acc);
			}
			acc.count++;
			acc.totalDist += ray.hitDistance;
			if (ray.blocked) acc.blockedCount++; else acc.openCount++;
		}
		const categories = Array.from(catMap.entries())
			.map(([category, acc]) => ({
				category,
				count: acc.count,
				blockedCount: acc.blockedCount,
				openCount: acc.openCount,
				avgHitDistance: acc.totalDist / acc.count,
				color: acc.color,
			}))
			.sort((a, b) => b.count - a.count);
		const blockedCount = nearbyRays.filter((r) => r.blocked).length;
		useThreeSceneStore.getState().setViewPlaneInspector({
			totalRays: nearbyRays.length,
			blockedCount,
			openCount: nearbyRays.length - blockedCount,
			categories,
		});
	};

	useEffect(() => {
		if (!activeSampleRef.current) {
			return;
		}

		const activeSample = activeSampleRef.current;
		updateInspectorForSample(
			activeSample.planeMesh,
			activeSample.localX,
			activeSample.localY,
			sampleScale
		);
	}, [sampleScale]);

	useEffect(() => {
		const canvas = canvasRef.current;
		const scene = sceneRef.current;
		const camera = cameraRef.current;
		const orbitControls = controlsRef.current?.controls;

		if (!canvas || !scene || !camera || !orbitControls || !sceneInitialized || tab.id !== 'daylight-views') {
			return;
		}

		const raycaster = new THREE.Raycaster();
		const mouse = new THREE.Vector2();
		const terrainMeshes = getTerrainMeshes(scene);

		const transformControls = new TransformControls(camera, canvas);
		transformControls.setSize(0.85);
		transformControls.setSpace('local');
		transformControls.setMode('rotate');
		const transformHelper = transformControls.getHelper();
		transformHelper.visible = false;
		markIgnoreSceneClick(transformHelper);
		scene.add(transformHelper);
		transformRef.current = transformControls;

		const hidePlaneControls = () => {
			transformControls.detach();
			transformHelper.visible = false;
			isDraggingRef.current = false;
			orbitControls.enabled = true;
		};

		const clearPlaneSelection = () => {
			useThreeSceneStore.getState().setSelectedViewPlaneStats(null);
			useThreeSceneStore.getState().setViewPlaneInspector(null);
			activeSampleRef.current = null;
			removeInspectorMarker();
		};

		const applyPlaneTransformUpdate = (object: THREE.Object3D, clampToTerrain: boolean) => {
			if (clampToTerrain && terrainMeshes.length > 0) {
				clampObjectAboveTerrain(object, scene, terrainMeshes);
			}
			updateSamplingPlaneFromTransform(object);
			useThreeSceneStore.getState().setSelectedViewPlaneStats(getSamplingPlaneStats(object));
			activeSampleRef.current = null;
			removeInspectorMarker();
			useThreeSceneStore.getState().setViewPlaneInspector(null);
		};

		const onDraggingChanged = (event: { value: unknown }) => {
			const dragging = Boolean(event.value);
			isDraggingRef.current = dragging;
			orbitControls.enabled = !dragging;

			if (!dragging && transformControls.object) {
				if (frameRequestRef.current !== null) {
					cancelAnimationFrame(frameRequestRef.current);
					frameRequestRef.current = null;
				}
				applyPlaneTransformUpdate(transformControls.object, true);
			}
		};

		const onObjectChange = () => {
			if (!transformControls.object) {
				return;
			}

			if (!isDraggingRef.current) {
				applyPlaneTransformUpdate(transformControls.object, true);
				return;
			}

			if (terrainMeshes.length > 0) {
				clampObjectAboveTerrain(transformControls.object, scene, terrainMeshes);
			}

			pendingObjectRef.current = transformControls.object;
			const now = performance.now();
			const DRAG_UPDATE_INTERVAL_MS = 75;
			if (now - lastDragUpdateMsRef.current < DRAG_UPDATE_INTERVAL_MS) {
				return;
			}
			if (frameRequestRef.current !== null) {
				return;
			}

			frameRequestRef.current = requestAnimationFrame(() => {
				frameRequestRef.current = null;
				lastDragUpdateMsRef.current = performance.now();
				if (pendingObjectRef.current) {
					applyPlaneTransformUpdate(pendingObjectRef.current, false);
				}
			});
		};

		const onPointerDown = (event: MouseEvent) => {
			const rect = canvas.getBoundingClientRect();
			mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
			mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

			raycaster.setFromCamera(mouse, camera);

			const planeMeshes: THREE.Object3D[] = [];
			scene.traverse((obj) => {
				if (obj.userData.isViewRaySamplingPlane) {
					planeMeshes.push(obj);
				}
			});

			if (planeMeshes.length === 0) {
				return;
			}

			const intersections = raycaster.intersectObjects(planeMeshes, true);
			const target = intersections.find((hit) => hit.object.userData.isViewRaySamplingPlane);
			if (target) {
				transformControls.attach(target.object);
				transformHelper.visible = true;
				useThreeSceneStore
					.getState()
					.setSelectedViewPlaneStats(getSamplingPlaneStats(target.object));

				// Inspector: find rays near the clicked local point on the plane
				const planeMesh = target.object as THREE.Mesh;
				const localHit = planeMesh.worldToLocal(target.point.clone());
				activeSampleRef.current = { planeMesh, localX: localHit.x, localY: localHit.y };
				updateInspectorForSample(planeMesh, localHit.x, localHit.y, useThreeSceneStore.getState().viewPlaneSampleScale);
			} else {
				activeSampleRef.current = null;
				removeInspectorMarker();
				useThreeSceneStore.getState().setViewPlaneInspector(null);
			}
		};

		const onContextMenu = (event: MouseEvent) => {
			if (!transformControls.object) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			transformControls.setMode(transformControls.mode === 'rotate' ? 'translate' : 'rotate');
		};

		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key.toLowerCase() === 'w' && transformControls.object) {
				transformControls.setMode('translate');
			}
			if (event.key.toLowerCase() === 'e' && transformControls.object) {
				transformControls.setMode('rotate');
			}
			if (event.key === 'Escape') {
				hidePlaneControls();
			}
		};

		const onClearVisualization = () => {
			hidePlaneControls();
			clearPlaneSelection();
		};

		transformControls.addEventListener('dragging-changed', onDraggingChanged);
		transformControls.addEventListener('objectChange', onObjectChange);
		canvas.addEventListener('mousedown', onPointerDown, true);
		canvas.addEventListener('contextmenu', onContextMenu, true);
		window.addEventListener('keydown', onKeyDown);
		window.addEventListener('__viewAnalysisClearVisualization__', onClearVisualization as EventListener);

		return () => {
			window.removeEventListener('keydown', onKeyDown);
			window.removeEventListener('__viewAnalysisClearVisualization__', onClearVisualization as EventListener);
			canvas.removeEventListener('mousedown', onPointerDown, true);
			canvas.removeEventListener('contextmenu', onContextMenu, true);
			transformControls.removeEventListener('dragging-changed', onDraggingChanged);
			transformControls.removeEventListener('objectChange', onObjectChange);
			if (frameRequestRef.current !== null) {
				cancelAnimationFrame(frameRequestRef.current);
				frameRequestRef.current = null;
			}
			hidePlaneControls();
			clearPlaneSelection();
			scene.remove(transformHelper);
			transformControls.dispose();
			transformRef.current = null;
		};
	}, [canvasRef, cameraRef, sceneRef, controlsRef, sceneInitialized, sceneVersion, tab]);
};
